'use client';

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal } from '../Modal';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs';
import { Input } from '@/components/forms/controls/Input';
import Dropdown from '@/components/forms/controls/Dropdown';
import { DateRangePicker } from '@/components/forms/controls/DateRangePicker';
import { Checkbox } from '@/components/forms/controls/Checkbox';
import Button from '@/components/ui-components/Button';
import { IncidentFilters } from '@/types/incident';
import { i18n } from '@/config/i18n';
import { DropdownOption } from '@/types/components';

const FilterModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const ModalHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  width: 36px;
  height: 36px;
  line-height: 0;
  transition: all 0.2s ease;
  border-radius: 6px;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const CloseIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export interface IncidentsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: IncidentFilters) => void;
  activeFilters?: IncidentFilters;
}

const originOptions: DropdownOption[] = [
  { label: i18n.triage.filters.options.allOrigins, value: 'all' },
  { label: i18n.triage.filters.options.monitor, value: 'MONITOR' },
  { label: i18n.triage.filters.options.scraper, value: 'SCRAPER' },
  { label: i18n.triage.filters.options.logAnalyzer, value: 'LOG_ANALYZER' },
];

const severityOptions: DropdownOption[] = [
  { label: i18n.triage.filters.options.allSeverities, value: 'all' },
  { label: i18n.triage.filters.options.critical, value: 'critical' },
  { label: i18n.triage.filters.options.high, value: 'high' },
  { label: i18n.triage.filters.options.medium, value: 'medium' },
  { label: i18n.triage.filters.options.low, value: 'low' },
];

const statusOptions: DropdownOption[] = [
  { label: i18n.triage.filters.options.allStatuses, value: 'all' },
  { label: i18n.triage.filters.options.open, value: 'Open' },
  { label: i18n.triage.filters.options.resolved, value: 'Resolved' },
];

const raisedFromAlertOptions: DropdownOption[] = [
  { label: i18n.triage.filters.options.allIncidents, value: 'all' },
  { label: i18n.triage.filters.options.raisedFromAlert, value: 'true' },
  { label: i18n.triage.filters.options.notFromAlert, value: 'false' },
];

export const IncidentsFilterModal: React.FC<IncidentsFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  activeFilters = {},
}) => {
  const [filters, setFilters] = useState<{
    incident_id: string;
    site_url: string;
    origin: string;
    severity: string;
    search: string;
    status: string;
    start_date: string;
    end_date: string;
    include_archived: boolean;
    raised_from_alert: string;
  }>({
    incident_id: activeFilters.incident_id || '',
    site_url: activeFilters.site_url || '',
    origin: activeFilters.origin || 'all',
    severity: activeFilters.severity || 'all',
    search: activeFilters.search || '',
    status: activeFilters.state || 'all',
    start_date: activeFilters.start_date || '',
    end_date: activeFilters.end_date || '',
    include_archived: activeFilters.include_archived || false,
    raised_from_alert: activeFilters.raised_from_alert === null ? 'all' : activeFilters.raised_from_alert === true ? 'true' : 'false',
  });

  // Update filters when activeFilters change
  useEffect(() => {
    if (isOpen) {
      setFilters({
        incident_id: activeFilters.incident_id || '',
        site_url: activeFilters.site_url || '',
        origin: activeFilters.origin || 'all',
        severity: activeFilters.severity || 'all',
        search: activeFilters.search || '',
        status: activeFilters.state || 'all',
        start_date: activeFilters.start_date || '',
        end_date: activeFilters.end_date || '',
        include_archived: activeFilters.include_archived || false,
        raised_from_alert: activeFilters.raised_from_alert === null ? 'all' : activeFilters.raised_from_alert === true ? 'true' : 'false',
      });
    }
  }, [activeFilters, isOpen]);

  const handleReset = () => {
    setFilters({
      incident_id: '',
      site_url: '',
      origin: 'all',
      severity: 'all',
      search: '',
      status: 'all',
      start_date: '',
      end_date: '',
      include_archived: false,
      raised_from_alert: 'all',
    });
  };

  const handleApply = () => {
    const appliedFilters: IncidentFilters = {
      ...(filters.incident_id && { incident_id: filters.incident_id }),
      ...(filters.site_url && { site_url: filters.site_url }),
      ...(filters.origin && filters.origin !== 'all' && { origin: filters.origin }),
      ...(filters.severity && filters.severity !== 'all' && { severity: filters.severity }),
      ...(filters.search && { search: filters.search }),
      ...(filters.status && filters.status !== 'all' && { status: filters.status }),
      ...(filters.start_date && { start_date: filters.start_date }),
      ...(filters.end_date && { end_date: filters.end_date }),
      ...(filters.include_archived && { include_archived: filters.include_archived }),
      ...(filters.raised_from_alert && filters.raised_from_alert !== 'all' && {
        raised_from_alert: filters.raised_from_alert === 'true' ? true : filters.raised_from_alert === 'false' ? false : null,
      }),
    };
    onApply(appliedFilters);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="600px">
      <ModalHeaderStyled>
        <Breadcrumbs
          items={[{ label: i18n.triage.filters.title }]}
          accentColor="#FB6F2A"
        />
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </ModalHeaderStyled>
      <FilterModalContent>
        <FilterSection>
          <Input
            label={i18n.triage.filters.incidents.incidentId}
            value={filters.incident_id || ''}
            onChange={(e) => setFilters({ ...filters, incident_id: e.target.value })}
            placeholder={i18n.triage.filters.placeholders.incidentId}
            fullWidth
          />
        </FilterSection>

        <FilterSection>
          <Input
            label={i18n.triage.filters.incidents.siteUrl}
            value={filters.site_url || ''}
            onChange={(e) => setFilters({ ...filters, site_url: e.target.value })}
            placeholder={i18n.triage.filters.placeholders.siteUrl}
            fullWidth
          />
        </FilterSection>

        <FilterSection>
          <Dropdown
            label={i18n.triage.filters.incidents.origin}
            options={originOptions}
            value={filters.origin || 'all'}
            onChange={(value) => setFilters({ ...filters, origin: value })}
            variant="form"
            useFixedPosition={true}
          />
        </FilterSection>

        <FilterSection>
          <Dropdown
            label={i18n.triage.filters.incidents.severity}
            options={severityOptions}
            value={filters.severity || 'all'}
            onChange={(value) => setFilters({ ...filters, severity: value })}
            variant="form"
            useFixedPosition={true}
          />
        </FilterSection>

        <FilterSection>
          <Input
            label={i18n.triage.filters.incidents.search}
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder={i18n.triage.filters.placeholders.search}
            fullWidth
          />
        </FilterSection>

        <FilterSection>
          <Dropdown
            label={i18n.triage.filters.incidents.status}
            options={statusOptions}
            value={filters.status || 'all'}
            onChange={(value) => setFilters({ ...filters, status: value })}
            variant="form"
            useFixedPosition={true}
          />
        </FilterSection>

        <FilterSection>
          <DateRangePicker
            label={i18n.common.labels.dateRange}
            startDate={filters.start_date || ''}
            endDate={filters.end_date || ''}
            onChange={(start, end) => {
              setFilters({ ...filters, start_date: start, end_date: end });
            }}
          />
        </FilterSection>

        <FilterSection>
          <Checkbox
            label={i18n.triage.filters.incidents.includeArchived}
            checked={filters.include_archived || false}
            onChange={(checked) => setFilters({ ...filters, include_archived: checked })}
            helpText="Include archived incidents from deleted sites. Default: false (only active incidents)"
          />
        </FilterSection>

        <FilterSection>
          <Dropdown
            label={i18n.triage.filters.incidents.raisedFromAlert}
            options={raisedFromAlertOptions}
            value={filters.raised_from_alert || 'all'}
            onChange={(value) => setFilters({ ...filters, raised_from_alert: value })}
            variant="form"
            useFixedPosition={true}
          />
        </FilterSection>

        <FilterActions>
          <Button variant="outline" onClick={handleReset}>
            {i18n.triage.filters.incidents.resetFilters}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {i18n.triage.filters.incidents.applyFilters}
          </Button>
        </FilterActions>
      </FilterModalContent>
    </Modal>
  );
};

export default IncidentsFilterModal;

